import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Message, SettingsCard } from "@humansignal/ui";
import { IconChevronDown } from "@humansignal/icons";
import { useAPI } from "@humansignal/core";
import { Menu } from "../../components/Menu/Menu";
import { Form, Input, Label } from "../../components/Form";
import { ProjectContext } from "../../providers/ProjectProvider";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { ConsentPurposesEditor } from "../../components/ConsentPurposesEditor/ConsentPurposesEditor";
import { Loading } from "../../components/Loading/Loading";
import { Modal } from "../../components/Modal/Modal";
import { cn } from "../../utils/bem";
import { useTranslation } from "../../i18n/LocaleProvider";

/**
 * Consent settings — project-scoped UI on top of the org-wide
 * ``consent.ConsentTemplate`` library. Mirrors the layout and tokens
 * of the other settings pages (SettingsPageHeader + SettingsCard
 * blocks) so the recorder, annotation, audio QC, and consent tabs
 * all read as one design system instead of four bespoke pages.
 *
 * Sections:
 *   1. Required for this project — list of templates marked required
 *      via ``ProjectConsentRequirement``.
 *   2. Library — every template the org owns; click "Require" to add
 *      it to section 1, "Edit" to bump the body / version.
 *   3. New / edit template — Markdown body, optional starter scaffold.
 *   4. Audit — verify the signature hash chain.
 */

const KIND_OPTIONS = [
  ["data_use", "Data reuse / dataset license"],
  ["contract", "Annotator contract"],
  ["nda", "Non-disclosure agreement"],
  ["ethics", "Ethics / IRB brief"],
  ["voice_release", "Voice / image release"],
  ["child_safeguard", "Child safeguarding"],
  ["payment_terms", "Payment terms"],
  ["custom", "Custom"],
];

export const ConsentSettings = () => {
  const { t } = useTranslation();
  const { project } = useContext(ProjectContext);
  const api = useAPI();

  const [templates, setTemplates] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [starters, setStarters] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [verifyResult, setVerifyResult] = useState(null);
  const [draft, setDraft] = useState({ id: null, title: "", kind: "custom", body: "", purposes: [] });
  // Template editor opens as a modal (owner-requested — mirrors the
  // org Consent library's editor).
  const [editorOpen, setEditorOpen] = useState(false);
  // Project-scoped signature audit log + tab filter ("All / Signed
  // / Withdrawn"). Loaded on demand the first time the manager
  // expands the audit card so we don't pay the round-trip on every
  // settings-page mount; `auditLoaded` flips after the first fetch.
  const [auditTab, setAuditTab] = useState("all");
  const [audit, setAudit] = useState(null);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditHasNext, setAuditHasNext] = useState(false);
  const AUDIT_PAGE_SIZE = 20;

  const refresh = useCallback(async () => {
    setError("");
    // Pass ``project`` so the library shown is the PROJECT'S org — not
    // the viewer's active org. A platform owner opening a project that
    // belongs to another org then sees (and can require) only that org's
    // templates, never a cross-org one the same-org guard would reject.
    const tplResp = await api.callApi("consentTemplates", {
      params: project?.id ? { project: project.id } : {},
    });
    if (tplResp && !tplResp.error) {
      setTemplates(Array.isArray(tplResp) ? tplResp : (tplResp.results ?? []));
    }
    if (project?.id) {
      const reqResp = await api.callApi("consentRequirements", { params: { project: project.id } });
      if (reqResp && !reqResp.error) {
        setRequirements(Array.isArray(reqResp) ? reqResp : (reqResp.results ?? []));
      }
    }
  }, [api, project?.id]);

  // The audit log is paginated server-side (DRF PageNumberPagination,
  // 50 rows/page, max 500). Refetched independently of the rest of
  // the page so navigating Prev/Next doesn't blow away the templates
  // / requirements lists. ``auditPage`` resets to 1 whenever the
  // tab filter changes — server-side filtering by ``is_withdrawal``
  // isn't supported (yet), so the UI tabs filter the current page
  // client-side and counts may slightly disagree with totals on
  // mixed pages. Acceptable for an audit-log surface; the underlying
  // DB has the source of truth.
  const refetchAudit = useCallback(async () => {
    if (!project?.id) return;
    setAudit(null);
    const r = await api.callApi("consentSignatures", {
      params: { project: project.id, page: auditPage, page_size: AUDIT_PAGE_SIZE },
    });
    if (!r || r.error) {
      // ``null`` falls through here too (network failure / endpoint
      // missing on a desktop Django that hasn't loaded the consent
      // app yet). Set an empty audit and bail out — without this
      // the next branch dereferences ``r`` and throws null-object.
      setAudit([]);
      setAuditTotal(0);
      setAuditHasNext(false);
      return;
    }
    if (Array.isArray(r)) {
      setAudit(r);
      setAuditTotal(r.length);
      setAuditHasNext(false);
    } else {
      setAudit(r?.results ?? []);
      setAuditTotal(typeof r?.count === "number" ? r.count : (r?.results?.length ?? 0));
      setAuditHasNext(!!r?.next);
    }
  }, [api, project?.id, auditPage]);

  useEffect(() => {
    refetchAudit();
  }, [refetchAudit]);

  // Whenever the tab filter changes, snap back to page 1 so a tab
  // switch never lands on an empty page that's only empty because
  // the server-side dataset has more rows further along.
  useEffect(() => {
    setAuditPage(1);
  }, [auditTab]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    let alive = true;
    api.callApi("consentStarters").then((r) => {
      if (!alive || r?.error) return;
      setStarters(r.starters || []);
    });
    return () => {
      alive = false;
    };
  }, [api]);

  const requiredTemplateIds = useMemo(() => new Set(requirements.map((r) => r.template)), [requirements]);
  const libraryUnused = useMemo(
    () => templates.filter((t) => !requiredTemplateIds.has(t.id) && !t.archived),
    [templates, requiredTemplateIds],
  );

  const auditCounts = useMemo(() => {
    if (!audit) return { signed: 0, withdrawn: 0, all: 0 };
    let s = 0;
    let w = 0;
    for (const r of audit) {
      if (r.is_withdrawal) w += 1;
      else s += 1;
    }
    return { signed: s, withdrawn: w, all: audit.length };
  }, [audit]);

  const auditVisible = useMemo(() => {
    if (!audit) return null;
    if (auditTab === "signed") return audit.filter((r) => !r.is_withdrawal);
    if (auditTab === "withdrawn") return audit.filter((r) => r.is_withdrawal);
    return audit;
  }, [audit, auditTab]);

  const auditDateFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }),
    [],
  );

  const onAddRequirement = async (templateId) => {
    setBusy(true);
    const r = await api.callApi("addConsentRequirement", {
      body: { project: project.id, template: templateId, required_for: "all", optional: false },
    });
    setBusy(false);
    if (r?.error) setError(r.response?.detail || "Could not add requirement");
    refresh();
  };

  const onRemoveRequirement = async (id) => {
    setBusy(true);
    const r = await api.callApi("deleteConsentRequirement", { params: { pk: id } });
    setBusy(false);
    if (r?.error) setError(r.response?.detail || "Could not remove requirement");
    refresh();
  };

  const onSubmitDraft = async () => {
    if (!draft.title.trim() || !draft.body.trim()) {
      setError("Title and body are required.");
      return;
    }
    setBusy(true);
    const body = {
      title: draft.title.trim(),
      kind: draft.kind,
      body: draft.body,
      purposes: (draft.purposes || []).filter((p) => p && p.key && p.label),
    };
    // Pass ``project`` so a template created / edited here is scoped to
    // the PROJECT'S org (not the viewer's active org) — it's created in,
    // and stays visible to, the org that owns this project.
    const projParam = project?.id ? { project: project.id } : {};
    const r = draft.id
      ? await api.callApi("updateConsentTemplate", { params: { pk: draft.id, ...projParam }, body })
      : await api.callApi("addConsentTemplate", { params: projParam, body });
    setBusy(false);
    if (r?.error) {
      setError(r.response?.detail || "Could not save template");
      return;
    }
    setDraft({ id: null, title: "", kind: "custom", body: "", purposes: [] });
    setEditorOpen(false);
    refresh();
  };

  const onEdit = (tpl) => {
    setDraft({
      id: tpl.id,
      title: tpl.title,
      kind: tpl.kind,
      body: tpl.body,
      purposes: Array.isArray(tpl.purposes) ? tpl.purposes : [],
    });
    setEditorOpen(true);
  };

  // Trigger a file download without navigating away. The export
  // endpoints return CSV with a Content-Disposition attachment header,
  // so a synthetic anchor click downloads in place (same as the old
  // <a target="_blank"> links, now driven from the split button).
  const downloadExport = (withBody = false) => {
    if (!project?.id) return;
    const url = `/api/consent/signatures/export/?project=${project.id}${withBody ? "&include_template_body=1" : ""}`;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onVerifyChain = async () => {
    if (!project?.organization) return;
    setVerifyResult(null);
    const r = await api.callApi("consentVerifyChain", { params: { org_id: project.organization } });
    if (r?.error) setVerifyResult({ intact: false, breaks: [{ reason: r.response?.detail || "request failed" }] });
    else setVerifyResult(r);
  };

  if (!project) return null;

  // Row block lifted from SettingsCard's internal rhythm: divider
  // border-top + flex layout, matches the AnnotationSettings + Audio QC pages.
  const rowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "var(--spacing-base, 12px) 0",
    borderTop: "1px solid var(--color-neutral-border-subtle)",
  };

  return (
    <div className={cn("settings-page").toClassName()}>
      <SettingsPageHeader
        title={t("pages.settings.consent_settings.consent")}
        subtitle={t("pages.settings.consent_settings.required_agreements_your_annotators_must")}
        actions={
          <Button
            type="button"
            variant="primary"
            size="small"
            onClick={() => {
              setDraft({ id: null, title: "", kind: "custom", body: "", purposes: [] });
              setEditorOpen(true);
            }}
          >
            + New template
          </Button>
        }
      />

      <div className={cn("settings-page").elem("body").toClassName()}>
        {error ? (
          <Message variant="negative" size="small">
            {error}
          </Message>
        ) : null}

        <SettingsCard
          title={t("pages.settings.consent_settings.required_for_this_project")}
          description={t("pages.settings.consent_settings.annotators_see_these_in_order_before_the")}
        >
          {requirements.length === 0 ? (
            <p
              style={{
                margin: 0,
                color: "var(--color-neutral-content-subtler)",
                fontSize: "var(--font-size-body-small)",
              }}
            >
              No agreements required yet. Add one from the library below.
            </p>
          ) : (
            requirements.map((r, i) => (
              <div key={r.id} style={i === 0 ? { ...rowStyle, borderTop: 0, paddingTop: 0 } : rowStyle}>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: "var(--font-size-body-small)" }}>{r.template_title}</div>
                  <div
                    style={{ fontSize: "var(--font-size-body-small)", color: "var(--color-neutral-content-subtler)" }}
                  >
                    {(KIND_OPTIONS.find((k) => k[0] === r.template_kind) || [undefined, "Custom"])[1]}, v
                    {r.template_version}, {r.optional ? "optional" : "blocking"}
                  </div>
                </div>
                <Button
                  variant="negative"
                  look="outlined"
                  size="small"
                  onClick={() => onRemoveRequirement(r.id)}
                  disabled={busy}
                >
                  Remove
                </Button>
              </div>
            ))
          )}
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.consent_settings.organization_template_library")}
          description={t("pages.settings.consent_settings.templates_not_yet_required_for_this_proj")}
        >
          {libraryUnused.length === 0 ? (
            <p
              style={{
                margin: 0,
                color: "var(--color-neutral-content-subtler)",
                fontSize: "var(--font-size-body-small)",
              }}
            >
              {templates.filter((t) => !t.archived).length === 0
                ? "No templates yet, use “+ New template” above to create one."
                : "All available templates are already required for this project."}
            </p>
          ) : (
            libraryUnused.map((t, i) => (
              <div key={t.id} style={i === 0 ? { ...rowStyle, borderTop: 0, paddingTop: 0 } : rowStyle}>
                <div style={{ flex: "1 1 auto", minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: "var(--font-size-body-small)" }}>{t.title}</div>
                  <div
                    style={{ fontSize: "var(--font-size-body-small)", color: "var(--color-neutral-content-subtler)" }}
                  >
                    {(KIND_OPTIONS.find((k) => k[0] === t.kind) || [undefined, "Custom"])[1]}, v{t.version}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="neutral" look="outlined" size="small" onClick={() => onEdit(t)}>
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    look="filled"
                    size="small"
                    onClick={() => onAddRequirement(t.id)}
                    disabled={busy}
                  >
                    Require
                  </Button>
                </div>
              </div>
            ))
          )}
        </SettingsCard>

        {editorOpen && (
          <Modal
            visible
            onHide={() => setEditorOpen(false)}
            title={draft.id ? "Edit template" : "New template"}
            style={{ width: 760 }}
          >
            <p
              style={{
                margin: "0 0 12px",
                fontSize: "var(--font-size-body-small)",
                color: "var(--color-neutral-content-subtler)",
              }}
            >
              {t("pages.settings.consent_settings.the_body_renders_as_markdown_to_the_anno")}
            </p>
            {!draft.id && starters.length ? (
              <Form.Row columnCount={1}>
                <Label text="Start from a vetted scaffold">
                  <select
                    onChange={(e) => {
                      const k = e.target.value;
                      if (!k) return;
                      const s = starters.find((x) => x.key === k);
                      if (s) setDraft({ id: null, title: s.title, kind: s.kind, body: s.body, purposes: [] });
                      e.target.value = "";
                    }}
                    defaultValue=""
                    style={{
                      height: 40,
                      minHeight: 40,
                      boxSizing: "border-box",
                      borderRadius: 5,
                      padding: "0 16px",
                      width: "100%",
                      border: "1px solid var(--color-neutral-border)",
                      background: "var(--color-neutral-background)",
                      fontSize: "var(--font-size-field-input)",
                      lineHeight: "var(--line-height-snug)",
                    }}
                  >
                    <option value="">{t("pages.settings.consent_settings.pick_a_starter")}</option>
                    {starters.map((s) => (
                      <option key={s.key} value={s.key}>
                        {s.title}
                      </option>
                    ))}
                  </select>
                </Label>
                <p
                  style={{
                    margin: 0,
                    fontSize: "var(--font-size-body-small)",
                    color: "var(--color-neutral-content-subtler)",
                    lineHeight: 1.45,
                  }}
                >
                  Scaffolds reference GDPR (EU), POPIA (South Africa), NDPA (Nigeria 2023), Kenya DPA, Ghana DPA (Act
                  843), the AU Malabo Convention, LGPD (Brazil), CCPA / CPRA (California), PIPL (China), and PIPEDA
                  (Canada). Replace the <code>{"{{ TOKENS }}"}</code> with your specifics, then have counsel licensed in
                  your jurisdiction review the final wording before publishing.
                </p>
              </Form.Row>
            ) : null}

            <Form.Row columnCount={2}>
              <Label text="Title" required>
                <Input
                  value={draft.title}
                  onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                  placeholder={t("pages.settings.consent_settings.eg_voice_corpus_reuse_research_only")}
                />
              </Label>
              <Label text="Type">
                <select
                  value={draft.kind}
                  onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                  style={{
                    height: 40,
                    minHeight: 40,
                    boxSizing: "border-box",
                    borderRadius: 5,
                    padding: "0 16px",
                    width: "100%",
                    border: "1px solid var(--color-neutral-border)",
                    background: "var(--color-neutral-background)",
                    fontSize: "var(--font-size-field-input)",
                    lineHeight: "var(--line-height-snug)",
                  }}
                >
                  {KIND_OPTIONS.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </Label>
            </Form.Row>

            <Form.Row columnCount={1}>
              <Label text="Body (Markdown)" required>
                <textarea
                  value={draft.body}
                  onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                  rows={12}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1px solid var(--color-neutral-border)",
                    borderRadius: 6,
                    fontFamily: "var(--font-family-mono, monospace)",
                    fontSize: "var(--font-size-body-small)",
                    lineHeight: 1.5,
                    resize: "vertical",
                    background: "var(--color-neutral-background)",
                  }}
                  placeholder={t("pages.settings.consent_settings.data_use10recordings_collected_on_this_p")}
                />
              </Label>
            </Form.Row>

            <div style={{ marginTop: 12 }}>
              <ConsentPurposesEditor
                value={draft.purposes || []}
                onChange={(purposes) => setDraft({ ...draft, purposes })}
                disabled={busy}
              />
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
              <Button variant="neutral" look="outlined" size="small" onClick={() => setEditorOpen(false)}>
                Cancel
              </Button>
              <Button variant="primary" look="filled" size="small" onClick={onSubmitDraft} disabled={busy}>
                {draft.id ? "Save changes" : "Create template"}
              </Button>
            </div>
          </Modal>
        )}

        <SettingsCard
          title={t("pages.settings.consent_settings.signature_withdrawal_history")}
          description={t("pages.settings.consent_settings.every_consent_action_recorded_for_this_p")}
        >
          {/* Tabs on the left, export split-button pinned to the right of
              the same row. Default click on the button exports the CSV;
              the appended caret opens the "with template body" variant.
              The CSV always returns the full project log (all rows, all
              actions) regardless of the active tab — per-tab filtering is
              a UI convenience and would be confusing baked into a file. */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 12,
              marginBottom: 12,
              borderBottom: "1px solid var(--color-neutral-border-subtle)",
            }}
          >
            <div
              role="tablist"
              aria-label={t("pages.settings.consent_settings.consent_audit_filter")}
              style={{ display: "flex", gap: 4 }}
            >
              {[
                ["all", `All (${auditCounts.all})`],
                ["signed", `Signed (${auditCounts.signed})`],
                ["withdrawn", `Withdrawn (${auditCounts.withdrawn})`],
              ].map(([key, label]) => {
                const selected = auditTab === key;
                return (
                  <button
                    key={key}
                    role="tab"
                    aria-selected={selected}
                    type="button"
                    onClick={() => setAuditTab(key)}
                    style={{
                      border: 0,
                      background: "transparent",
                      padding: "8px 12px",
                      marginBottom: -1,
                      cursor: "pointer",
                      fontSize: "var(--font-size-body-small)",
                      fontWeight: selected ? 600 : 500,
                      color: selected ? "var(--color-primary-content)" : "var(--color-neutral-content)",
                      borderBottom: selected ? "2px solid var(--color-primary-content)" : "2px solid transparent",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* Export split button */}
            <div style={{ display: "inline-flex", flexShrink: 0, marginBottom: 6 }}>
              <Button
                variant="neutral"
                look="outlined"
                size="small"
                onClick={() => downloadExport(false)}
                title={t("pages.settings.consent_settings.download_every_consent_action_recorded_f")}
                style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
              >
                Export CSV
              </Button>
              <Dropdown.Trigger
                align="right"
                content={
                  <Menu size="compact">
                    <Menu.Item onClick={() => downloadExport(true)}>Export CSV (with template body)</Menu.Item>
                  </Menu>
                }
              >
                <Button
                  variant="neutral"
                  look="outlined"
                  size="small"
                  aria-label="More export options"
                  style={{
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderLeft: 0,
                    paddingLeft: 6,
                    paddingRight: 6,
                  }}
                >
                  <IconChevronDown />
                </Button>
              </Dropdown.Trigger>
            </div>
          </div>

          {audit == null ? (
            <Loading minHeight={160} />
          ) : auditVisible.length === 0 ? (
            <p
              style={{
                margin: 0,
                color: "var(--color-neutral-content-subtler)",
                fontSize: "var(--font-size-body-small)",
              }}
            >
              {auditTab === "signed"
                ? "No signatures recorded for this project yet."
                : auditTab === "withdrawn"
                  ? "No withdrawals recorded for this project yet."
                  : "No consent actions recorded for this project yet."}
            </p>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-body-small)" }}>
                <thead>
                  <tr
                    style={{
                      textAlign: "left",
                      color: "var(--color-neutral-content-subtler)",
                      fontSize: "var(--font-size-body-small)",
                    }}
                  >
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>{t("pages.settings.consent_settings.when")}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>Annotator</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>
                      {t("pages.settings.consent_settings.template")}
                    </th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>
                      {t("pages.settings.consent_settings.action")}
                    </th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>
                      {t("pages.settings.consent_settings.country")}
                    </th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>{t("pages.settings.consent_settings.age")}</th>
                    <th style={{ padding: "6px 8px", fontWeight: 500 }}>{t("pages.settings.consent_settings.pdf")}</th>
                  </tr>
                </thead>
                <tbody>
                  {auditVisible.map((r) => (
                    <tr key={r.id} style={{ borderTop: "1px solid var(--color-neutral-border-subtle)" }}>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {auditDateFormat.format(new Date(r.signed_at))}
                      </td>
                      <td style={{ padding: "6px 8px" }}>{r.user_email || `#${r.user}`}</td>
                      <td style={{ padding: "6px 8px" }}>
                        {r.template_title}{" "}
                        <span style={{ color: "var(--color-neutral-content-subtler)" }}>v{r.template_version}</span>
                      </td>
                      <td
                        style={{
                          padding: "6px 8px",
                          color: r.is_withdrawal ? "var(--color-negative-content)" : "var(--color-positive-content)",
                          fontWeight: 500,
                        }}
                      >
                        {r.is_withdrawal ? "Withdrew" : `Signed${r.signed_name ? ` as “${r.signed_name}”` : ""}`}
                      </td>
                      <td style={{ padding: "6px 8px" }}>{r.country_of_residence || "—"}</td>
                      <td style={{ padding: "6px 8px" }}>
                        {r.age_attestation ? r.age_attestation.replace(/_/g, " ") : "—"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <a
                          href={`/api/consent/signatures/${r.id}/pdf/`}
                          target="_blank"
                          rel="noopener"
                          style={{
                            color: "var(--color-primary-content)",
                            textDecoration: "underline",
                            fontSize: "var(--font-size-body-small)",
                          }}
                          title={t("pages.settings.consent_settings.download_a_pdf_receipt_of_this_signature")}
                        >
                          Download
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination footer. Hidden when there's only one page so
              short logs don't show a chrome-only row. The current
              tab counts the page's filtered rows (server doesn't
              filter by is_withdrawal), but ``auditTotal`` is the
              server-reported total across pages — managers can see
              "Page X of ~Y" at a glance even when the tab narrows
              the current view. */}
          {audit && audit.length > 0 && (auditHasNext || auditPage > 1) ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                marginTop: 12,
                paddingTop: 12,
                borderTop: "1px solid var(--color-neutral-border-subtle)",
                fontSize: "var(--font-size-body-small)",
              }}
            >
              <span style={{ color: "var(--color-neutral-content-subtler)" }}>
                Page {auditPage}
                {auditTotal ? (
                  <>
                    , showing {(auditPage - 1) * AUDIT_PAGE_SIZE + 1}–
                    {Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotal)} of {auditTotal}
                  </>
                ) : null}
              </span>
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="neutral"
                  look="outlined"
                  size="small"
                  onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                  disabled={auditPage <= 1}
                >
                  ← Previous
                </Button>
                <Button
                  variant="neutral"
                  look="outlined"
                  size="small"
                  onClick={() => setAuditPage((p) => p + 1)}
                  disabled={!auditHasNext}
                >
                  Next →
                </Button>
              </div>
            </div>
          ) : null}
        </SettingsCard>

        <SettingsCard
          title={t("pages.settings.consent_settings.audit_signature_log")}
          description={t("pages.settings.consent_settings.run_a_verify_pass_on_this_organizations_")}
        >
          <Button variant="neutral" look="outlined" size="small" onClick={onVerifyChain}>
            Verify chain
          </Button>
          {verifyResult ? (
            <div style={{ marginTop: 12 }}>
              {verifyResult.intact ? (
                <Message variant="positive" size="small">
                  {t("pages.settings.consent_settings.chain_intact_every_signature_verifies")}
                </Message>
              ) : (
                <Message variant="negative" size="small">
                  {verifyResult.breaks.length} break(s) detected:
                  <ul style={{ margin: "6px 0 0 18px" }}>
                    {verifyResult.breaks.map((b, i) => (
                      <li key={i}>
                        row {b.id ?? "?"}: {b.reason}
                      </li>
                    ))}
                  </ul>
                </Message>
              )}
            </div>
          ) : null}
        </SettingsCard>
      </div>
    </div>
  );
};

ConsentSettings.title = "Consent";
ConsentSettings.path = "/consent";
ConsentSettings.exact = true;
